import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ApplicationService, CyberSecurityOrgService } from '@pt/api-client';
import { PenTestDialog } from '../../../models/pen-test-dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Application } from 'it/primatoo/openapi/api/org/schemas/application/getapplicationformenuresponse/Application';
import { forkJoin, Observable, Subject } from 'rxjs';
import { ListApplicationForMenuResponse } from 'it/primatoo/openapi/api/org/schemas/application/ListApplicationForMenuResponse';
import { ListUserColumnsResponse } from 'it/primatoo/openapi/api/org/schemas/common/ListUserColumnsResponse';
import {
  CellValueChangedEvent,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
} from 'ag-grid-community';
import { ag_grid_frameworkComponents } from '@pt/pt-shared';
import { PRIMATOO_BASE_PROD_ENVIRONMENT, PtCoreSelectors } from '@pt/pt-core';
import { Store } from '@ngrx/store';
import { ThreatValue } from 'it/primatoo/openapi/api/org/schemas/common/ThreatValue';
import {
  AG_GRID_LOCALE_EN,
  IoniconsFilled,
  IoniconsOutline,
} from '@pt/pt-models';
import { takeUntil } from 'rxjs/operators';
import { PenTestSeverity } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/PenTestSeverity';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';
import { UpdateAppPenTestRequest } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/UpdateAppPenTestRequest';
import { PenTestUtils, PenVulnerabilityValidator } from './pen-test-utils';
import { PenetrationTest } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/_summary/PenetrationTest';
import { AppPenTest } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/AppPenTest';
import { TranslateService } from '@ngx-translate/core';
import { UniquePenTestVulnerability } from './pen-test-dialog.model';

@Component({
  selector: 'pt-pen-test-dialog',
  templateUrl: './pen-test-dialog.component.html',
  styleUrls: ['pen-test-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PenTestDialogComponent implements OnInit, OnDestroy {
  @Input() penTestDialog: PenTestDialog;
  @ViewChild('actionCol', { static: true }) actionCol: TemplateRef<unknown>;

  public currentLanguage$: Observable<string> = this.store.select(
    PtCoreSelectors.currentLanguage
  );
  public loading = true;
  public errorCode = 0;
  private gridApi!: GridApi;
  public threats: Array<ThreatValue> = [];
  public threatEvalLabels: Array<string> = PenTestUtils.threatEvalLabels();
  public gridOption!: GridOptions;
  public IconClose = IoniconsFilled.close;
  public IconReload = IoniconsFilled.refresh;
  public IconAdd = IoniconsOutline.plus;
  public enableChange = false;
  private rowData: UniquePenTestVulnerability[] = [];
  private rowId = 0;

  public formGroup = new FormGroup({
    applicationId: new FormControl(null, [Validators.required]),
    author: new FormControl(null, [Validators.required]),
    startedAt: new FormControl(Date.now() - 60 * 1000, [Validators.required]),
    completedAt: new FormControl(Date.now(), [Validators.required]),
    note: new FormControl(''),
    vulnerabilities: new FormControl(this.rowData, [
      PenVulnerabilityValidator(),
    ]),
  });

  private destroy$: Subject<boolean> = new Subject<boolean>();
  public applications: Array<Application> = [];
  private columnDefs: ColDef[];
  public appPenTest?: AppPenTest;
  public errorStartDateMessage?: Observable<string>;
  public errorCompletedDateMessage?: Observable<string>;

  constructor(
    public activeModal: NgbActiveModal,
    private store: Store,
    private translateService: TranslateService,
    private applicationService: ApplicationService,
    private cyberSecurityOrgService: CyberSecurityOrgService
  ) {}

  ngOnInit(): void {
    this.load();
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.enableChange = true;
      const startedAt = this.formGroup.controls.startedAt.value;
      const completedAt = this.formGroup.controls.completedAt.value;
      this.errorStartDateMessage = undefined;
      this.errorCompletedDateMessage = undefined;
      if (!startedAt) {
        this.errorStartDateMessage = this.translateService.stream(
          'PAGE.PEN_TEST.DIALOG.REQUIRED'
        );
      }
      if (!completedAt) {
        this.errorCompletedDateMessage = this.translateService.stream(
          'PAGE.PEN_TEST.DIALOG.REQUIRED'
        );
      }
      if (startedAt && completedAt && startedAt >= completedAt) {
        this.errorCompletedDateMessage = this.translateService.stream(
          'PAGE.PEN_TEST.DIALOG.START_GREATER_END'
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  private align() {
    this.gridOption = undefined;
    const rowData = JSON.parse(
      JSON.stringify(
        this.rowData.map((r, i) => {
          return { ...r, rowUniqueId: i };
        })
      )
    );
    this.rowId = rowData.length + 1;
    const baseOptions: GridOptions = {
      ...PRIMATOO_BASE_PROD_ENVIRONMENT.defaultGridOptions,
      rowData,
      columnDefs: this.columnDefs,
      localeText: AG_GRID_LOCALE_EN,
      suppressPropertyNamesCheck: true,
      components: ag_grid_frameworkComponents,
      rowBuffer: 5,
      pinnedBottomRowData: [rowData ? rowData.length : 0],
      fullWidthCellRenderer: 'footerRenderComponent',
      isFullWidthRow: (params) => params.rowNode.rowPinned === 'bottom',
      getContextMenuItems: () =>
        PRIMATOO_BASE_PROD_ENVIRONMENT.defaultContextMenuItems,
    } as GridOptions;
    this.gridOption = baseOptions;

    setTimeout(() => {
      if (this.appPenTest && this.gridApi) {
        this.gridApi.setRowData([]);
        const uniqueVulnerability: UniquePenTestVulnerability[] =
          PenTestUtils.vulnerabilities2uniquePTVulnerabilityList(
            this.appPenTest.vulnerabilities
          );
        uniqueVulnerability.forEach((v: UniquePenTestVulnerability) => {
          this.add(v);
        });
        this.loading = false;
      } else {
        this.loading = false;
      }
    }, 300);
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  public cellValueChanged(params: CellValueChangedEvent) {
    const rowValue: UniquePenTestVulnerability[] = [];
    this.gridApi.forEachNode((r) => {
      rowValue.push(r.data);
    });
    this.formGroup.controls.vulnerabilities.setValue(rowValue);
  }

  upsert(close?: boolean) {
    this.loading = true;
    this.errorCode = 0;
    const formValue = this.formGroup.getRawValue();
    const applicationId = formValue.applicationId;
    const request: UpdateAppPenTestRequest = {
      author: formValue.author,
      startedAt: formValue.startedAt,
      completedAt: formValue.completedAt,
      note: formValue.note,
      vulnerabilities:
        PenTestUtils.uniquePTVulnerabilityList2PTVulnerabilityList(
          formValue.vulnerabilities
        ),
    };
    this.cyberSecurityOrgService
      .updateAppPetTest(applicationId, request)
      .subscribe(
        () => {
          this.loading = false;
          if (close) {
            this.close(true);
          } else {
            this.cyberSecurityOrgService
              .getAppPetTest(this.appPenTest.appId)
              .subscribe((s) => {
                this.appPenTest = s;
              });
          }
        },
        () => {
          this.loading = false;
          this.errorCode = 1;
        }
      );
  }

  close(value: boolean) {
    this.destroy$.next(true);
    this.activeModal.close(value);
  }

  private load() {
    this.enableChange = false;
    if (this.penTestDialog.penTest) {
      this.loadUpdate(this.penTestDialog.penTest);
    } else {
      this.loadCreate();
    }
  }

  private loadUpdate(penetrationTest: PenetrationTest) {
    forkJoin({
      menu: this.applicationService.getMenu(),
      listUserColumnsResponse: this.cyberSecurityOrgService.getPetTestColumns(),
      appPenTest: this.cyberSecurityOrgService.getAppPetTest(
        penetrationTest.appId
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: {
          menu: ListApplicationForMenuResponse;
          listUserColumnsResponse: ListUserColumnsResponse;
          appPenTest: AppPenTest;
        }) => {
          this.appPenTest = response.appPenTest;
          this.formGroup.setValue({
            applicationId: this.appPenTest.appId,
            author: this.appPenTest.author,
            startedAt: this.appPenTest.startedAt,
            completedAt: this.appPenTest.completedAt,
            note: this.appPenTest.note,
            vulnerabilities: [],
          });
          this.formGroup.controls.applicationId.disable();
          const columns: Array<ColumnWithLocalization> =
            response.listUserColumnsResponse.columns;
          this.applications = response.menu.applications.filter(
            (f) => f.id === this.appPenTest.appId
          );
          this.columnDefs = this.generateColumns(columns);
          this.loading = false;
          this.align();
        }
      );
  }

  private loadCreate() {
    forkJoin({
      menu: this.applicationService.getMenu(),
      listUserColumnsResponse: this.cyberSecurityOrgService.getPetTestColumns(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: {
          menu: ListApplicationForMenuResponse;
          listUserColumnsResponse: ListUserColumnsResponse;
        }) => {
          let filterAppId: number[] = [];
          if (
            this.penTestDialog &&
            this.penTestDialog.penetrationTests &&
            this.penTestDialog.penetrationTests?.length > 0
          ) {
            filterAppId = this.penTestDialog.penetrationTests.map(
              (p) => p.appId
            );
          }
          this.applications = response.menu.applications.filter(
            (f) => !filterAppId.includes(f.id)
          );
          this.columnDefs = this.generateColumns(
            response.listUserColumnsResponse.columns
          );
          this.loading = false;
          this.align();
        }
      );
  }

  reload() {
    this.load();
  }

  removeRow(rowNode) {
    const row = rowNode.row;
    if (this.gridApi && !!row && rowNode) {
      this.gridApi.applyTransaction({ remove: [row] });
      const rowValue: UniquePenTestVulnerability[] = [];
      this.gridApi.forEachNode((r) => {
        rowValue.push(r.data);
      });
      this.formGroup.controls.vulnerabilities.setValue(rowValue);
      this.gridApi.setPinnedBottomRowData([rowValue.length]);
    }
  }

  add(vulnerability?: UniquePenTestVulnerability) {
    if (this.gridApi) {
      const rowData: UniquePenTestVulnerability[] = [];
      this.gridApi.forEachNode((rowNode) => {
        rowData.push(rowNode.data);
      });

      if (vulnerability) {
        rowData.push(vulnerability);
      } else {
        const newRow: UniquePenTestVulnerability = {
          rowUniqueId: this.rowId,
          uuid: 'R_' + this.rowId,
          name: undefined,
          severity: PenTestSeverity.NONE,
          description: undefined,
          remedy: undefined,
          resolved: false,
          confidentialityViolation: 0,
          integrityCompromise: 0,
          availabilityLoss: 0,
        };
        rowData.push(newRow);
      }

      this.gridApi.setRowData(rowData);
      const rowValue: UniquePenTestVulnerability[] = [];
      this.gridApi.forEachNode((r) => {
        rowValue.push(r.data);
      });
      this.formGroup.controls.vulnerabilities.setValue(rowValue);
      this.gridApi.setPinnedBottomRowData([rowValue.length]);
    }
    this.rowId++;
  }

  private generateColumns(columns: Array<ColumnWithLocalization>): ColDef[] {
    const response: ColDef[] = [];
    columns.forEach((column) => {
      let col: ColDef;
      switch (column.column) {
        case 'severity':
          col = PenTestUtils.colDefSeverity(column, true);
          break;
        case 'confidentialityViolation':
        case 'integrityCompromise':
        case 'availabilityLoss':
          col = PenTestUtils.colDefThreat(column, true);
          break;
        case 'description':
        case 'remedy':
          col = PenTestUtils.colDefTextArea(column, true);
          break;
        case 'id':
        case 'uuid':
        case 'name':
          col = PenTestUtils.colDefText(column, true);
          break;
        case 'resolved':
          col = PenTestUtils.colDefCheckBox(column, true);
          break;

        default:
      }
      response.push(col);
    });
    response.push(PenTestUtils.colDefAction(this.actionCol));
    return response;
  }
}
